'use client';

import React, { useEffect } from 'react';

import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';

import Lines from '@/app/[locale]/(manual)/guide/[slug]/Lines';
import { MediaDetail } from '@/app/[locale]/(manual)/guide/[slug]/MediaDetail';
import Navigation from '@/app/[locale]/(manual)/guide/[slug]/Navigation';
import { useNavigation } from '@/app/[locale]/(manual)/guide/[slug]/useNavigation';
import GuideCategory from '@/app/[locale]/(manual)/manual/[slug]/GuideCategory';
import EditInAdminButton from '@/ui/components/EditInAdminButton';
import Loader from '@/ui/components/Loader';
import {
  ManualLoading,
  ManualMainMain,
  ManualMain,
  ManualContent,
  ManualPhoto,
  ManualPhotoControls,
  ManualPhotoSlider,
  ManualPhotoSliderImg,
} from '@/ui/components/Manual/manual.styled';
import { useManual } from '@/ui/components/Manual/ManualContextProvider';
import { preloadImages } from '@/utils/helpers';
import { getYoutubeId } from '@/utils/helpers';

interface ManualProps {
  category: CategoryData;
  guides: GuideData[];
  pdfs: PdfData[];
}

export default function Manual({ category, guides, pdfs }: ManualProps) {
  const locale = useLocale();
  const router = useRouter();
  const [isHovered, setIsHovered] = React.useState(false);

  const {
    activeStep,
    activePhoto,
    setActivePhoto,
    setActiveStep,
    sidebar,
    steps,
    guide,
    siblings,
  } = useManual();

  // Use the navigation hook to handle all navigation logic
  const {
    hasPrevSlideOrStep,
    hasNextSlideOrStep,
    currentStep,
    currentStepGallery,
    currentStepVideo,
    prevSlide,
    nextSlide,
    prevStep,
    nextStep,
    isLoading,
  } = useNavigation({
    locale,
    activeStep,
    activePhoto,
    setActiveStep,
    setActivePhoto,
    steps,
    guide,
    siblings,
  });

  const updateMediaUrls = function (data) {
    data.forEach(item => {
      if (item.media?.gallery) {
        item.media.gallery.forEach(galleryItem => {
          updateUrlsInGalleryItem(galleryItem);
          if (galleryItem.child) {
            updateUrlsInGalleryItem(galleryItem.child);
          }
        });
      }
    });
    return data;
  };

  function updateUrlsInGalleryItem(item) {
    const updateUrl = url =>
      url.replace('cdn.pkb.local', 'cdn.help.prusa3d.com');

    [
      'original',
      'medium',
      'large',
      'thumbnail',
      'medium_large',
      '1536x1536',
    ].forEach(key => {
      if (item[key]) item[key] = updateUrl(item[key]);
    });

    if (item.srcset) {
      item.srcset = item.srcset
        .split(', ')
        .map(src => updateUrl(src))
        .join(', ');
    }
  }

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      updateMediaUrls(steps);
    }
  }, [steps]);

  useEffect(() => {
    if (!steps?.[activeStep]?.media?.gallery) return;

    const imagesToPreload = steps[activeStep].media.gallery.map(
      img => img.medium_large
    );
    preloadImages(imagesToPreload);

    setActivePhoto(0);
  }, [steps]);

  useEffect(() => {
    if (location.hash === '#last' && steps?.length) {
      // Set to last step without adding to history
      const lastStepIndex = steps.length - 1;
      setActiveStep(lastStepIndex);
      if (steps[lastStepIndex]?.media?.gallery) {
        setActivePhoto(steps[lastStepIndex].media.gallery.length - 1);
      }
      router.replace(
        `${location.pathname}${
          steps[lastStepIndex]?.id ? `#${steps[lastStepIndex].id}` : ''
        }`,
        { scroll: false }
      );
    }
  }, [steps]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if focus is in an input or textarea
      const activeElement = document.activeElement;
      const isInputActive =
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement;

      // Check if any modifier key is also pressed
      const isModifierPressed =
        event.altKey || event.ctrlKey || event.shiftKey || event.metaKey;

      // Only handle shortcuts if not in input/textarea and no modifier keys
      if (!isInputActive && !isModifierPressed) {
        switch (event.key) {
          case 'ArrowLeft':
            prevStep();
            break;
          case 'ArrowRight':
            nextStep();
            break;
          case 'ArrowUp':
            prevSlide();
            break;
          case 'ArrowDown':
            if (hasNextSlideOrStep) {
              nextSlide();
            }
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeStep, activePhoto]);

  if (!currentStepGallery && !currentStepVideo) return null;

  if (!steps[activeStep]) return null;

  return (
    <ManualMainMain>
      {isLoading && (
        <ManualLoading>
          <Loader />
        </ManualLoading>
      )}
      <ManualMain>
        {/* Entry step for non-assembly manuals */}
        {steps?.[activeStep]?.entryStep ? (
          <GuideCategory
            category={category}
            entryStep={true}
            nextStep={steps?.[1]?.id ?? null}
            guides={guides}
            pdfs={pdfs}
          />
        ) : (
          <>
            <ManualContent onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
              <EditInAdminButton item={currentStep} visible={isHovered} parentSlug={guide?.slug} />
              <Lines data={currentStep.lines} />
            </ManualContent>
            <ManualPhoto $isTabOpen={sidebar !== null}>
              {currentStepGallery ? (
                <MediaDetail
                  imageData={currentStep.media.gallery[activePhoto]?.child || currentStep.media.gallery[activePhoto]}
                  //src={currentStep.media.gallery[activePhoto]?.medium_large || null}
                  alt={currentStep?.title || ''}
                  isTabOpen={sidebar !== null}
                />
              ) : (
                currentStepVideo && (
                  <MediaDetail
                    youtubeSrc={currentStepVideo}
                    alt={currentStep?.title || ''}
                    isTabOpen={sidebar !== null}
                  />
                )
              )}
              <ManualPhotoControls>
                <ManualPhotoSlider>
                  {currentStepGallery
                    ? currentStepGallery.map((img, i) => (
                        <ManualPhotoSliderImg
                          key={img.id}
                          src={img.child?.thumbnail || img.thumbnail}
                          onClick={() => setActivePhoto(i)}
                          $isActive={activePhoto === i}
                        />
                      ))
                    : currentStepVideo && (
                        <ManualPhotoSliderImg
                          src={`https://img.youtube.com/vi/${getYoutubeId(currentStepVideo)}/default.jpg`}
                          $isActive={true}
                        />
                      )}
                </ManualPhotoSlider>

                <Navigation
                  isTabOpen={sidebar !== null}
                  activePhoto={activePhoto}
                  hasPrevSlideOrStep={hasPrevSlideOrStep}
                  hasNextSlideOrStep={hasNextSlideOrStep}
                  currentStepGallery={currentStepGallery}
                  currentStepVideo={currentStepVideo}
                  prevSlide={prevSlide}
                  nextSlide={nextSlide}
                  prevStep={prevStep}
                  nextStep={nextStep}
                />
              </ManualPhotoControls>
            </ManualPhoto>
          </>
        )}
      </ManualMain>
    </ManualMainMain>
  );
}
